import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    IconButton,
    Modal,
    Paper,
    Box,
    Typography,
    Divider,
    Grid,
    Fade,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import PieChartIcon from '@mui/icons-material/PieChart';
import CloseIcon from '@mui/icons-material/Close';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie, Doughnut } from 'react-chartjs-2';
import { getTripSummary, tripSummarySelector, tripSummaryLoadingSelector } from '../../../features/tripcenter/tripControlSlice'; // Adjust import path as needed
import { distancePeriodsMain, getperiod } from '../../../utils/datePeriodCalculator';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';


// Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend);

const TripSummaryModal = () => {
    const dispatch = useDispatch();
    const [open, setOpen] = useState(false);
    const loading = useSelector(tripSummaryLoadingSelector);
    // You'll need to create this selector to access trip summary data
    const tripSummary = useSelector(tripSummarySelector) || {};

    // Add state for selected period
    const [selectedPeriod, setSelectedPeriod] = useState('thismonthtonow');
    const [previousPeriod, setPreviousPeriod] = useState('thismonthtonow');
    const [customDialogOpen, setCustomDialogOpen] = useState(false);
    const [customFromDate, setCustomFromDate] = useState(null);
    const [customToDate, setCustomToDate] = useState(null);
    const [customDateError, setCustomDateError] = useState('');
    const [customRangeApplied, setCustomRangeApplied] = useState(false);
    const [appliedCustomFromDate, setAppliedCustomFromDate] = useState(null);
    const [appliedCustomToDate, setAppliedCustomToDate] = useState(null);

    const periodOptions = [...distancePeriodsMain, { name: 'Custom Range', value: 'custom' }];

    // Color palettes for charts
    const statusColors = ['#3498db', '#2ecc71', '#f39c12', '#e74c3c', '#9b59b6'];
    const typeColors = ['#1abc9c', '#3498db', '#9b59b6', '#f1c40f', '#e67e22'];

    const openCustomDialog = () => {
        const basePeriod = selectedPeriod === 'custom' ? previousPeriod : selectedPeriod;
        if (appliedCustomFromDate && appliedCustomToDate) {
            setCustomFromDate(appliedCustomFromDate);
            setCustomToDate(appliedCustomToDate);
        } else if (!customFromDate || !customToDate) {
            const { fromDate, toDate } = getperiod(basePeriod) || {};
            setCustomFromDate(fromDate ? dayjs(fromDate.toDate()) : dayjs());
            setCustomToDate(toDate ? dayjs(toDate.toDate()) : dayjs());
        }
        setCustomDateError('');
        setCustomDialogOpen(true);
    };

    const handleCustomDialogClose = () => {
        setCustomDialogOpen(false);
        setCustomDateError('');
        if (!customRangeApplied) {
            setSelectedPeriod(previousPeriod);
            setCustomFromDate(null);
            setCustomToDate(null);
            return;
        }
        if (appliedCustomFromDate && appliedCustomToDate) {
            setCustomFromDate(appliedCustomFromDate);
            setCustomToDate(appliedCustomToDate);
        }
    };

    const handleCustomRangeSubmit = () => {
        if (!customFromDate || !customToDate) {
            setCustomDateError('Please select both dates.');
            return;
        }

        if (customFromDate.isAfter(customToDate)) {
            setCustomDateError('From date cannot be after To date.');
            return;
        }

        setCustomDateError('');
        setSelectedPeriod('custom');
        setCustomRangeApplied(true);
        setAppliedCustomFromDate(customFromDate);
        setAppliedCustomToDate(customToDate);

        dispatch(getTripSummary({
            startDate: customFromDate.format('YYYY-MM-DD'),
            endDate: customToDate.format('YYYY-MM-DD')
        }));
        setCustomDialogOpen(false);
    };

    // Period change handler
    const handlePeriodChange = (event) => {
        const newPeriod = event.target.value;
        setPreviousPeriod(selectedPeriod);
        if (newPeriod === 'custom') {
            setSelectedPeriod('custom');
            openCustomDialog();
            return;
        }
        setCustomRangeApplied(false);
        setSelectedPeriod(newPeriod);

        // Get date range for selected period
        const { fromDate, toDate } = getperiod(newPeriod);
        if (!fromDate || !toDate) {
            return;
        }

        // Dispatch summary request with new dates
        dispatch(getTripSummary({
            startDate: fromDate.format('YYYY-MM-DD'),
            endDate: toDate.format('YYYY-MM-DD')
        }));
    };

    const handleOpen = async () => {
        setOpen(true);

        if (selectedPeriod === 'custom') {
            if (appliedCustomFromDate && appliedCustomToDate) {
                dispatch(getTripSummary({
                    startDate: appliedCustomFromDate.format('YYYY-MM-DD'),
                    endDate: appliedCustomToDate.format('YYYY-MM-DD')
                }));
            } else {
                openCustomDialog();
            }
            return;
        }

        // Get date range for current selected period
        const { fromDate, toDate } = getperiod(selectedPeriod);
        if (!fromDate || !toDate) {
            return;
        }

        dispatch(getTripSummary({
            startDate: fromDate.format('YYYY-MM-DD'),
            endDate: toDate.format('YYYY-MM-DD')
        }));
    };

    const handleClose = () => {
        setOpen(false);
    };

    // Prepare chart data from summary
    const tripStatusData = {
        labels: ['Active', 'Completed', 'Pending', 'Cancelled', 'Delayed'],
        datasets: [{
            data: [
                tripSummary.activeTrips || 0,
                tripSummary.completedTrips || 0,
                tripSummary.pendingTrips || 0,
                tripSummary.cancelledTrips || 0,
                tripSummary.delayedTrips || 0
            ],
            backgroundColor: statusColors,
            borderColor: statusColors.map(color => color + '80'),
            borderWidth: 1,
        }]
    };

    const tripTypeData = {
        labels: ['One-Way', 'Round Trip', 'Multi-Stop', 'Recurring', 'Special'],
        datasets: [{
            data: [
                tripSummary.oneWayTrips || 0,
                tripSummary.roundTrips || 0,
                tripSummary.multiStopTrips || 0,
                tripSummary.recurringTrips || 0,
                tripSummary.specialTrips || 0
            ],
            backgroundColor: typeColors,
            borderColor: typeColors.map(color => color + '80'),
            borderWidth: 1,
        }]
    };

    // Helper functions for new data structure
    const getDestinationsList = () => tripSummary.topDestinations?.list || [];
    const getTrucksList = () => tripSummary.topTrucks?.list || [];
    const getDriversList = () => tripSummary.activeDrivers?.list || [];

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'right',
                labels: {
                    boxWidth: 12,
                    padding: 15,
                    font: {
                        size: 11
                    }
                }
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const label = context.label || '';
                        const value = context.raw || 0;
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
                        return `${label}: ${value} (${percentage}%)`;
                    }
                }
            }
        }
    };

    // Stats card component
    const StatCard = ({ title, value, color, icon }) => (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1
            }}
        >
            <Box
                sx={{
                    bgcolor: color + '20',
                    color: color,
                    p: 1,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                {icon}
            </Box>
            <Typography variant="h6" fontWeight="bold" color={color}>
                {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                {title}
            </Typography>
        </Paper>
    );

    // Find the current period name for display
    const getCurrentPeriodName = () => {
        if (selectedPeriod === 'custom') {
            return 'Custom Range';
        }
        const period = distancePeriodsMain.find(p => p.value === selectedPeriod);
        return period ? period.name : 'Custom Period';
    };

    // Get formatted date range for display
    const getDateRangeString = () => {
        if (selectedPeriod === 'custom') {
            if (appliedCustomFromDate && appliedCustomToDate) {
                return `${appliedCustomFromDate.format('MMM D, YYYY')} - ${appliedCustomToDate.format('MMM D, YYYY')}`;
            }
            return 'Select custom dates';
        }
        const { fromDate, toDate } = getperiod(selectedPeriod);
        if (!fromDate || !toDate) {
            return 'Not available';
        }
        return `${fromDate.format('MMM D, YYYY')} - ${toDate.format('MMM D, YYYY')}`;
    };

    return (
        <>
            <IconButton
                onClick={handleOpen}
                sx={{
                    bgcolor: open ? 'primary.main' : 'transparent',
                    color: open ? 'white' : 'primary.main',
                    padding: 1,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        bgcolor: open ? 'primary.dark' : 'action.hover'
                    }
                }}
            >
                <PieChartIcon fontSize="small" />
            </IconButton>

            <Dialog
                open={customDialogOpen}
                onClose={handleCustomDialogClose}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle sx={{ fontWeight: 700, textAlign: 'center' }}>Select Custom Dates</DialogTitle>
                <DialogContent dividers>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <DatePicker
                                    label="From date"
                                    value={customFromDate}
                                    onChange={(newValue) => setCustomFromDate(newValue)}
                                    format="DD MMM YYYY"
                                    slotProps={{
                                        textField: { fullWidth: true, size: 'small' }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <DatePicker
                                    label="To date"
                                    value={customToDate}
                                    onChange={(newValue) => setCustomToDate(newValue)}
                                    format="DD MMM YYYY"
                                    slotProps={{
                                        textField: { fullWidth: true, size: 'small' }
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </LocalizationProvider>
                    {customDateError && (
                        <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                            {customDateError}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handleCustomDialogClose} color="inherit">Cancel</Button>
                    <Button variant="contained" onClick={handleCustomRangeSubmit}>Apply</Button>
                </DialogActions>
            </Dialog>

            <Modal
                open={open}
                onClose={handleClose}
                closeAfterTransition
            >
                <Fade in={open}>
                    <Paper
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '90%',
                            maxWidth: 1000,
                            maxHeight: '90vh',
                            overflow: 'auto',
                            bgcolor: 'background.paper',
                            borderRadius: 2,
                            boxShadow: 24,
                            p: 0
                        }}
                    >
                        {/* Header with Period Selector */}
                        <Box
                            sx={{
                                p: 2,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}
                        >
                            <Typography variant="h6" fontWeight="bold">
                                Trip Summary Dashboard
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {/* Period Selector */}
                                <FormControl
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        minWidth: 180,
                                        '& .MuiOutlinedInput-root': {
                                            color: 'white',
                                            '& fieldset': {
                                                borderColor: 'rgba(255, 255, 255, 0.5)',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: 'white',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: 'white',
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: 'white',
                                        }
                                    }}
                                >
                                    <InputLabel id="period-selector-label">Period</InputLabel>
                                    <Select
                                        labelId="period-selector-label"
                                        id="period-selector"
                                        value={selectedPeriod}
                                        onChange={handlePeriodChange}
                                        label="Period"
                                        startAdornment={<DateRangeIcon sx={{ mr: 1 }} />}
                                    >
                                        {periodOptions.map((period) => (
                                            <MenuItem
                                                key={period.value}
                                                value={period.value}
                                                onClick={period.value === 'custom' ? () => {
                                                    if (selectedPeriod === 'custom') {
                                                        openCustomDialog();
                                                    }
                                                } : undefined}
                                            >
                                                {period.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <IconButton size="small" onClick={handleClose} sx={{ color: 'white' }}>
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </Box>

                        {/* Period detail banner */}
                        <Box
                            sx={{
                                bgcolor: 'primary.50',
                                p: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            <DateRangeIcon color="primary" fontSize="small" />
                            <Typography variant="body2" color="primary.dark">
                                Showing data for: <strong>{getCurrentPeriodName()}</strong> ({getDateRangeString()})
                            </Typography>
                        </Box>

                        {/* Content */}
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Box sx={{ p: 3 }}>
                                    {/* Key Metrics */}
                                    <Box sx={{ mb: 4 }}>
                                        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
                                            Operational Overview
                                        </Typography>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} sm={6} md={2.4}>
                                            <StatCard
                                                title="Total Trips"
                                                value={tripSummary.totalTrips || 0}
                                                color="#3498db"
                                                icon={<PieChartIcon />}
                                            />
                                        </Grid>
                                            <Grid item xs={12} sm={6} md={2.4}>
                                            <StatCard
                                                    title="Active Trips"
                                                value={tripSummary.activeTrips || 0}
                                                color="#2ecc71"
                                                icon={<PieChartIcon />}
                                            />
                                        </Grid>
                                            <Grid item xs={12} sm={6} md={2.4}>
                                            <StatCard
                                                    title="Pending Trips"
                                                    value={tripSummary.pendingTrips || 0}
                                                    color="#f39c12"
                                                    icon={<PieChartIcon />}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={2.4}>
                                                <StatCard
                                                    title="Delayed"
                                                    value={tripSummary.delayedTrips || 0}
                                                    color="#e74c3c"
                                                    icon={<PieChartIcon />}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={2.4}>
                                                <StatCard
                                                    title="Completed Trips"
                                                    value={tripSummary.completedTrips || 0}
                                                color="#9b59b6"
                                                icon={<PieChartIcon />}
                                            />
                                        </Grid>
                                        </Grid>
                                    </Box>

                                    {/* Fleet & Resources */}
                                    <Box sx={{ mb: 4 }}>
                                        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
                                            Fleet & Resources
                                        </Typography>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} sm={3}>
                                                <StatCard
                                                    title="Total Distance"
                                                    value={`${Math.round(tripSummary.totalDistance || 0).toLocaleString('en-IN')} Km`}
                                                    color="#e74c3c"
                                                    icon={<PieChartIcon />}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={3}>
                                                <StatCard
                                                    title="Destinations Served"
                                                    value={tripSummary.totalDestinations || 0}
                                                    color="#1abc9c"
                                                    icon={<PieChartIcon />}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={3}>
                                            <StatCard
                                                    title="Trucks Ran"
                                                    value={tripSummary.totalTrucks || 0}
                                                    color="#34495e"
                                                    icon={<PieChartIcon />}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={3}>
                                                <StatCard
                                                    title="Drivers Worked"
                                                    value={tripSummary.totalDrivers || 0}
                                                    color="#e67e22"
                                                icon={<PieChartIcon />}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>

                                    <Divider sx={{ my: 4 }} />

                                    {/* Top Routes */}
                                    <Box sx={{ mb: 4 }}>
                                        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
                                            Top Routes Performance
                                        </Typography>
                                        <Paper
                                            elevation={0}
                                            variant="outlined"
                                            sx={{
                                                p: 0,
                                                borderRadius: 3,
                                                overflow: 'hidden',
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                                            }}
                                        >
                                            {/* Header */}
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr',
                                                    borderBottom: 1,
                                                    borderColor: 'divider',
                                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                    pr: 1.7,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        p: 2.5,
                                                        fontWeight: 600,
                                                        fontSize: '1rem',
                                                        color: 'white',
                                                        textAlign: 'center',
                                                        borderRight: 1,
                                                        borderColor: 'rgba(255,255,255,0.2)',
                                                    }}
                                                >
                                                    Route Name
                                                </Box>
                                                <Box
                                                    sx={{
                                                        p: 2.5,
                                                        fontWeight: 600,
                                                        fontSize: '1rem',
                                                        color: 'white',
                                                        textAlign: 'center',
                                                        borderRight: 1,
                                                        borderColor: 'rgba(255,255,255,0.2)',
                                                    }}
                                                >
                                                    Total Trips
                                                </Box>
                                                <Box
                                                    sx={{
                                                        p: 2.5,
                                                        fontWeight: 600,
                                                        fontSize: '1rem',
                                                        color: 'white',
                                                        textAlign: 'center',
                                                        borderRight: 1,
                                                        borderColor: 'rgba(255,255,255,0.2)',
                                                    }}
                                                >
                                                    Completed
                                                </Box>
                                                <Box
                                                    sx={{
                                                        p: 2.5,
                                                        fontWeight: 600,
                                                        fontSize: '1rem',
                                                        color: 'white',
                                                        textAlign: 'center',
                                                        borderRight: 1,
                                                        borderColor: 'rgba(255,255,255,0.2)',
                                                    }}
                                                >
                                                    Active
                                                </Box>
                                                <Box
                                                    sx={{
                                                        p: 2.5,
                                                        fontWeight: 600,
                                                        fontSize: '1rem',
                                                        color: 'white',
                                                        textAlign: 'center',
                                                        borderRight: 1,
                                                        borderColor: 'rgba(255,255,255,0.2)',
                                                    }}
                                                >
                                                    Pending
                                                </Box>
                                                <Box
                                                    sx={{
                                                        p: 2.5,
                                                        fontWeight: 600,
                                                        fontSize: '1rem',
                                                        color: 'white',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    Km Run
                                                </Box>
                                            </Box>

                                            {/* Data Rows */}
                                            <Box sx={{ maxHeight: '300px', overflow: 'auto' }}>
                                                {tripSummary.topRoutesWithBreakdown && tripSummary.topRoutesWithBreakdown.length > 0 ? (
                                                    tripSummary.topRoutesWithBreakdown.map((route, index) => (
                                                        <Box
                                                            key={index}
                                                            sx={{
                                                                display: 'grid',
                                                                gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr',
                                                                borderBottom: index < tripSummary.topRoutesWithBreakdown.length - 1 ? 1 : 0,
                                                                borderColor: 'divider',
                                                                '&:hover': { bgcolor: 'action.hover' }
                                                            }}
                                                        >
                                                            {/* Route Name */}
                                                            <Box
                                                                sx={{
                                                                    p: 2,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 1.5,
                                                                    borderRight: 1,
                                                                    borderColor: 'divider',
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        width: 28,
                                                                        height: 28,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        borderRadius: '50%',
                                                                        bgcolor: 'primary.50',
                                                                        color: 'primary.main',
                                                                        fontSize: '0.875rem',
                                                                        fontWeight: 'bold'
                                                                    }}
                                                                >
                                                                    {index + 1}
                                                                </Box>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontWeight: 500,
                                                                        color: 'text.primary',
                                                                        lineHeight: 1.3
                                                                    }}
                                                                >
                                                                    {route.routeName} <b>[#{route.routeNumber}]</b>
                                                                </Typography>
                                                            </Box>

                                                            {/* Total Trips */}
                                                            <Box
                                                                sx={{
                                                                    p: 2,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    borderRight: 1,
                                                                    borderColor: 'divider',
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        color: 'primary.main',
                                                                        bgcolor: 'primary.50',
                                                                        px: 1.5,
                                                                        py: 0.5,
                                                                        borderRadius: 1,
                                                                        fontSize: '0.875rem'
                                                                    }}
                                                                >
                                                                    {route.totalTrips}
                                                                </Typography>
                                                            </Box>

                                                            {/* Completed Trips */}
                                                            <Box
                                                                sx={{
                                                                    p: 2,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    borderRight: 1,
                                                                    borderColor: 'divider',
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        color: 'success.main',
                                                                        bgcolor: 'success.50',
                                                                        px: 1.5,
                                                                        py: 0.5,
                                                                        borderRadius: 1,
                                                                        fontSize: '0.875rem'
                                                                    }}
                                                                >
                                                                    {route.completedTrips}
                                                                </Typography>
                                                            </Box>

                                                            {/* Active Trips */}
                                                            <Box
                                                                sx={{
                                                                    p: 2,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    borderRight: 1,
                                                                    borderColor: 'divider',
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        color: 'warning.main',
                                                                        bgcolor: 'warning.50',
                                                                        px: 1.5,
                                                                        py: 0.5,
                                                                        borderRadius: 1,
                                                                        fontSize: '0.875rem'
                                                                    }}
                                                                >
                                                                    {route.activeTrips}
                                                                </Typography>
                                                            </Box>

                                                            {/* Pending Trips */}
                                                            <Box
                                                                sx={{
                                                                    p: 2,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    borderRight: 1,
                                                                    borderColor: 'divider',
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        color: 'error.main',
                                                                        bgcolor: 'error.50',
                                                                        px: 1.5,
                                                                        py: 0.5,
                                                                        borderRadius: 1,
                                                                        fontSize: '0.875rem'
                                                                    }}
                                                                >
                                                                    {route.pendingTrips}
                                                                </Typography>
                                                            </Box>

                                                            {/* Distance */}
                                                            <Box
                                                                sx={{
                                                                    p: 2,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        color: 'info.main',
                                                                        fontSize: '0.875rem'
                                                                    }}
                                                                >
                                                                    {Math.round(route.totalKilometers || 0).toLocaleString('en-IN')}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    ))
                                                ) : (
                                                    <Box sx={{ p: 3, textAlign: 'center' }}>
                                                        <Typography variant="body2" color="text.disabled">No route data available</Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Paper>
                                </Box>

                                    <Divider sx={{ my: 4 }} />




                                    {/* Performance Insights */}
                                <Box>
                                        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
                                            Performance Insights
                                        </Typography>
                                    <Paper
                                        elevation={0}
                                        variant="outlined"
                                            sx={{
                                                p: 0,
                                                borderRadius: 3,
                                                overflow: 'hidden',
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                                            }}
                                    >
                                            {/* Fixed layout container to address scrollbar issue */}
                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                {/* Section Tabs - Using grid with fixed column sizes to match data rows */}
                                                <Box
                                                    sx={{
                                                        display: 'grid',
                                                        gridTemplateColumns: '1fr 1fr 1fr',
                                                        borderBottom: 1,
                                                        borderColor: 'divider',
                                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                        pr: 1.7, // Add padding to account for scrollbar width
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            p: 2.5,
                                                            fontWeight: 600,
                                                            fontSize: '1rem',
                                                            color: 'white',
                                                            textAlign: 'center',
                                                            borderRight: 1,
                                                            borderColor: 'rgba(255,255,255,0.2)',
                                                        }}
                                                    >
                                                        Top Destinations
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            p: 2.5,
                                                            fontWeight: 600,
                                                            fontSize: '1rem',
                                                            color: 'white',
                                                            textAlign: 'center',
                                                            borderRight: 1,
                                                            borderColor: 'rgba(255,255,255,0.2)',
                                                        }}
                                                    >
                                                        Most Active Trucks
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            p: 2.5,
                                                            fontWeight: 600,
                                                            fontSize: '1rem',
                                                            color: 'white',
                                                            textAlign: 'center',
                                                        }}
                                                    >
                                                        Active Drivers
                                                    </Box>
                                                </Box>

                                                {/* Data Rows - Using the same grid layout as headers */}
                                                <Box sx={{ maxHeight: '250px', overflow: 'auto' }}>
                                                    <Box sx={{
                                                        display: 'grid',
                                                        gridTemplateColumns: '1fr 1fr 1fr', // Same grid pattern as header
                                                    }}>
                                                        {/* Destinations Column */}
                                                        <Box sx={{ borderRight: 1, borderColor: 'divider' }}>
                                                            {getDestinationsList().length > 0 ? (
                                                                getDestinationsList().map((dest, index) => (
                                                                <Box
                                                                    key={index}
                                                                    sx={{
                                                                        p: 2,
                                                                        display: 'flex',
                                                                        justifyContent: 'space-between',
                                                                        alignItems: 'center',
                                                                        borderBottom: index < getDestinationsList().length - 1 ? 1 : 0,
                                                                        borderColor: 'divider',
                                                                        '&:hover': { bgcolor: 'action.hover' }
                                                                    }}
                                                                >
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                                        <Box
                                                                            sx={{
                                                                                width: 28,
                                                                                height: 28,
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                borderRadius: '50%',
                                                                                bgcolor: 'primary.50',
                                                                                color: 'primary.main',
                                                                                fontSize: '0.875rem',
                                                                                fontWeight: 'bold'
                                                                            }}
                                                                        >
                                                                            {index + 1}
                                                                        </Box>
                                                                        <Typography variant="body2" noWrap sx={{ maxWidth: '130px' }}>
                                                                            {dest.name}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            fontWeight: 600,
                                                                            color: 'primary.main',
                                                                            bgcolor: 'primary.50',
                                                                            px: 1,
                                                                            py: 0.5,
                                                                            borderRadius: 1,
                                                                            fontSize: '0.75rem'
                                                                        }}
                                                                    >
                                                                        {dest.count}
                                                                    </Typography>
                                                                </Box>
                                                            ))
                                                            ) : (
                                                                    <Box sx={{ p: 3, textAlign: 'center' }}>
                                                                        <Typography variant="body2" color="text.disabled">No data available</Typography>
                                                                    </Box>
                                                            )}
                                                        </Box>

                                                        {/* Trucks Column */}
                                                        <Box sx={{ borderRight: 1, borderColor: 'divider' }}>
                                                            {getTrucksList().length > 0 ? (
                                                                getTrucksList().map((truck, index) => (
                                                                <Box
                                                                    key={index}
                                                                    sx={{
                                                                        p: 2,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'space-between',
                                                                        borderBottom: index < getTrucksList().length - 1 ? 1 : 0,
                                                                        borderColor: 'divider',
                                                                        '&:hover': { bgcolor: 'action.hover' }
                                                                    }}
                                                                >
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                                                                        <Box
                                                                            sx={{
                                                                                width: 28,
                                                                                height: 28,
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                borderRadius: '50%',
                                                                                bgcolor: 'success.50',
                                                                                color: 'success.main',
                                                                                fontSize: '0.875rem',
                                                                                fontWeight: 'bold'
                                                                            }}
                                                                        >
                                                                            {index + 1}
                                                                        </Box>
                                                                            <Typography variant="body2" noWrap sx={{ flex: 1, maxWidth: '120px' }}>
                                                                            {truck.number}
                                                                        </Typography>
                                                                    </Box>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{
                                                                                    fontWeight: 600,
                                                                                    color: 'info.main',
                                                                                    fontSize: '0.75rem'
                                                                                }}
                                                                            >
                                                                                {Math.round(truck.totalKilometers || 0).toLocaleString('en-IN')} km
                                                                            </Typography>
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{
                                                                                    fontWeight: 600,
                                                                                    color: 'success.main',
                                                                                    bgcolor: 'success.50',
                                                                                    px: 1,
                                                                                    py: 0.5,
                                                                                    borderRadius: 1,
                                                                                    fontSize: '0.75rem'
                                                                                }}
                                                                            >
                                                                                ({truck.tripCount})
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                            ))
                                                            ) : (
                                                                    <Box sx={{ p: 3, textAlign: 'center' }}>
                                                                        <Typography variant="body2" color="text.disabled">No data available</Typography>
                                                                    </Box>
                                                            )}
                                                        </Box>

                                                        {/* Drivers Column */}
                                                        <Box>
                                                            {getDriversList().length > 0 ? (
                                                                getDriversList().map((driver, index) => (
                                                                <Box
                                                                    key={index}
                                                                    sx={{
                                                                        p: 2,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'space-between',
                                                                        borderBottom: index < getDriversList().length - 1 ? 1 : 0,
                                                                        borderColor: 'divider',
                                                                        '&:hover': { bgcolor: 'action.hover' }
                                                                    }}
                                                                >
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                                                                            <Box
                                                                                sx={{
                                                                                    width: 28,
                                                                                    height: 28,
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    borderRadius: '50%',
                                                                                    bgcolor: 'info.50',
                                                                                    color: 'info.main',
                                                                                    fontSize: '0.875rem',
                                                                                    fontWeight: 'bold'
                                                                                }}
                                                                            >
                                                                                {index + 1}
                                                                            </Box>
                                                                            <Typography variant="body2" noWrap sx={{ flex: 1, maxWidth: '120px' }}>
                                                                                {driver.name}
                                                                            </Typography>
                                                                        </Box>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{
                                                                                    fontWeight: 600,
                                                                                    color: 'warning.main',
                                                                                    fontSize: '0.75rem'
                                                                                }}
                                                                            >
                                                                                {Math.round(driver.totalKilometers || 0).toLocaleString('en-IN')} km
                                                                            </Typography>
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{
                                                                                    fontWeight: 600,
                                                                                    color: 'info.main',
                                                                                    bgcolor: 'info.50',
                                                                                    px: 1,
                                                                                    py: 0.5,
                                                                                    borderRadius: 1,
                                                                                    fontSize: '0.75rem'
                                                                                }}
                                                                            >
                                                                                ({driver.trips || 0})
                                                                            </Typography>
                                                                        </Box>
                                                                </Box>
                                                            ))
                                                            ) : (
                                                                    <Box sx={{ p: 3, textAlign: 'center' }}>
                                                                        <Typography variant="body2" color="text.disabled">No data available</Typography>
                                                                    </Box>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Box>
                                    </Paper>
                                </Box>

                                    <Divider sx={{ my: 4 }} />
                                    {/* Analytics Dashboard */}
                                    <Box sx={{ mb: 4 }}>
                                        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
                                            Analytics Dashboard
                                        </Typography>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} md={6}>
                                                <Paper
                                                    elevation={0}
                                                    variant="outlined"
                                                    sx={{
                                                        p: 3,
                                                        height: 350,
                                                        borderRadius: 3,
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
                                                    }}
                                                >
                                                    <Typography variant="h6" fontWeight="600" gutterBottom textAlign="center" sx={{ mb: 2 }}>
                                                        Trip Status Distribution
                                                    </Typography>
                                                    <Box sx={{ height: 280 }}>
                                                        <Doughnut data={tripStatusData} options={chartOptions} />
                                                    </Box>
                                                </Paper>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Paper
                                                    elevation={0}
                                                    variant="outlined"
                                                    sx={{
                                                        p: 3,
                                                        height: 350,
                                                        borderRadius: 3,
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
                                                    }}
                                                >
                                                    <Typography variant="h6" fontWeight="600" gutterBottom textAlign="center" sx={{ mb: 2 }}>
                                                        Trip Type Analysis
                                                    </Typography>
                                                    <Box sx={{ height: 280 }}>
                                                        <Pie data={tripTypeData} options={chartOptions} />
                                                    </Box>
                                                </Paper>
                                            </Grid>
                                        </Grid>
                                    </Box>
                            </Box>
                        )}
                    </Paper>
                </Fade>
            </Modal>
        </>
    );
};

export default TripSummaryModal;
